import { useMemo } from 'react';

import type { IndividualPaymentParcelViewModel } from '@/shared/api/generated';

import { getParcelYear } from '../utils/getParcelYear';

/**
 * Anos de vencimento disponíveis nas parcelas de um plano, em ordem
 * decrescente.
 *
 * É a lista de opções do seletor "Ano" — que existe só porque o endpoint de
 * parcelas devolve a coleção completa do contrato. Não há endpoint que informe
 * em que anos o plano tem parcelas.
 *
 * O ano sai de `getParcelYear` (fatia da string ISO), e não de `dayjs`, para
 * que uma parcela que vence em 1º de janeiro não caia no ano anterior.
 *
 * @param parcels - Parcelas do plano selecionado.
 * @returns Anos distintos, do mais recente para o mais antigo.
 *
 * @example
 * ```tsx
 * const years = useConsolidationYears(parcels); // [2024, 2023, 2022]
 * ```
 */
export function useConsolidationYears(
  parcels: readonly IndividualPaymentParcelViewModel[],
): readonly number[] {
  return useMemo(() => {
    const years = new Set<number>();
    for (const parcel of parcels) {
      const year = getParcelYear(parcel.expirationDate);
      if (year !== null) years.add(year);
    }
    return [...years].sort((a, b) => b - a);
  }, [parcels]);
}
